import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Home,
  Users, 
  Thermometer,
  Bed,
  Coffee,
  Droplets,
  ArrowRight,
  Phone,
  CheckCircle,
  Star
} from "lucide-react";
import SEOHead from "../components/SEOHead";

const GroupAccommodationPage = () => {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    "name": "Umoona Underground Group Accommodation",
    "description": "Unique underground accommodation for groups in Coober Pedy. 24°C constant temperature, bunkhouse accommodation for up to 102 people.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "14 Hutchison St",
      "addressLocality": "Coober Pedy",
      "addressRegion": "SA",
      "postalCode": "5723",
      "addressCountry": "AU"
    },
    "url": "https://umoonaopalmine.com.au/group-accommodation"
  };

  const features = [
    {
      icon: Bed,
      title: "Bunk Beds & Mattresses",
      description: "Comfortable bunk beds with quality mattresses in every room"
    },
    {
      icon: Coffee,
      title: "Kitchen Facilities", 
      description: "Fridge, sink, kettle, and toaster provided for each group"
    },
    {
      icon: Droplets,
      title: "Group Facilities",
      description: "Toilet facilities and water metered showers for each group"
    },
    {
      icon: CheckCircle,
      title: "Linen Available",
      description: "Clean linen can be supplied if required for your stay"
    }
  ];

  const groupTypes = [
    {
      title: "School Groups",
      description: "Educational trips with safe, supervised accommodation. Perfect for learning about Australian mining history and geology."
    },
    {
      title: "Tour Groups", 
      description: "Coach groups and organized tours can enjoy comfortable underground accommodation as part of their Coober Pedy experience."
    },
    {
      title: "Special Groups",
      description: "Corporate retreats, club outings, and special interest groups seeking a unique underground Australian experience."
    }
  ];

  const accommodationImages = [
    {
      src: "/assets/DEL_4290.jpg",
      alt: "Underground Bunkhouse",
      caption: "24°C Constant Temperature"
    },
    {
      src: "/assets/DEL_4292.jpg", 
      alt: "Bunkhouse Interior",
      caption: "Comfortable Living Space"
    },
    {
      src: "/assets/DEL_4282.jpg",
      alt: "Underground Rooms",
      caption: "Group Accommodation"
    },
    {
      src: "/assets/DEL_4283.jpg",
      alt: "Underground Bunkhouse",
      caption: "Underground Living"
    },
    {
      src: "/assets/DEL_4280.jpg",
      alt: "Underground Accommodation",
      caption: "Climate Controlled"
    }
  ];

  return (
    <div>
      <SEOHead
        title="Underground Group Accommodation - Coober Pedy | Umoona Opal Mine"
        description="Unique underground accommodation for groups in Coober Pedy. 24°C constant temperature, bunkhouse accommodation for up to 102 people."
        keywords="underground accommodation, group accommodation Coober Pedy, school group accommodation, underground bunkhouses"
        canonical="/group-accommodation"
        schemaData={schemaData}
      />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/assets/DEL_4290.jpg"
            alt="Underground Group Accommodation at Umoona"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/50 to-black/70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="max-w-4xl">
            <Badge variant="secondary" className="mb-6 bg-blue-500/20 text-blue-300 border border-blue-400/30 px-6 py-3 text-lg">
              🏠 Underground Living • 24°C Constant Temperature
            </Badge>
            
            <h1 className="text-4xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Commercial Group Tours
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-green-400">
                & Accommodation
              </span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              Umoona Opal Mine offers unique underground accommodation for groups big and small.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">102</div>
                <div className="text-blue-200 font-semibold">Max Beds</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">24°C</div>
                <div className="text-green-200 font-semibold">Constant</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">4-6</div>
                <div className="text-yellow-200 font-semibold">Per Room</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="mining-cta font-bold px-8 py-4 text-lg">
                <Link to="/contact">
                  Contact for Info
                  <Phone className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline" 
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-bold px-8 py-4 text-lg backdrop-blur-sm"
              >
                <Link to="/group-tours">
                  Learn About Group Tours
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Underground Bunkhouse Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Underground Bunkhouse Accommodation
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              We provide accommodation which consists of underground bunkhouses, 4 to 6 share. 
              Each bunkhouse is of various size, and our largest has 102 beds.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900">Accommodation Features</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  Underground bunkhouses, 4 to 6 share
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  Largest bunkhouse has 102 beds
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  Consistent 24°C temperature year-round
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  All rooms have bunk beds with mattresses
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  Linen can be supplied if required
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  Toilet facilities for each group
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  Fridge, sink, kettle, toaster provided
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  Water metered showers available
                </li>
              </ul>
            </div>

            <Card className="p-6 bg-blue-50 border-blue-200">
              <CardContent className="space-y-4 p-0">
                <Thermometer className="w-12 h-12 text-blue-600 mx-auto" />
                <h3 className="text-xl font-bold text-blue-800 text-center">Comfort & Climate</h3>
                <p className="text-blue-700 text-center">
                  Extremely comfortable for sleeping, with temperatures maintaining a consistent 24°C degrees. 
                  Perfect for year-round stays regardless of Coober Pedy's extreme surface weather conditions.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Accommodation Gallery */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Underground Accommodation Gallery
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {accommodationImages.map((image, index) => (
              <div key={index} className="group relative overflow-hidden rounded-xl shadow-lg">
                <img 
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <h4 className="font-bold text-lg">{image.alt}</h4>
                  <p className="text-sm">{image.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Included Amenities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Included Amenities
            </h2>
            <p className="text-xl text-gray-600">
              Everything your group needs for a comfortable underground stay
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="p-6 text-center hover:shadow-lg transition-shadow">
                <CardContent className="space-y-4 p-0">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                    <feature.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Perfect for Groups */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Perfect for Group Travel
            </h2>
            <p className="text-xl text-gray-600">
              Ideal accommodation for various types of group experiences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {groupTypes.map((group, index) => (
              <Card key={index} className="p-6">
                <CardContent className="space-y-4 p-0">
                  <div className="bg-gradient-to-br from-purple-100 to-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                    <Users className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-center">{group.title}</h3>
                  <p className="text-gray-600 text-center">{group.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl lg:text-4xl font-bold">
                Ready for Your Underground Group Experience?
              </h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Contact us to learn more about our unique underground accommodation and plan your authentic Coober Pedy group experience.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8 py-4 text-lg"
              >
                <Link to="/contact">
                  Contact for Group Booking
                  <Phone className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold px-8 py-4 text-lg"
              >
                <Link to="/group-tours">
                  Learn About Group Tours
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GroupAccommodationPage;